// HighLowPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { API_BASE_URL } from '../config';
import RefreshButton from './RefreshButton';
import WatchlistStar from './WatchlistStar';
import StockLogo from './StockLogo';
import "./HighLowPage.css";


const HighLowPage = ({ onBack }) => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter] = useState("FO"); // FO or ALL (both act the same for now)
  const [direction, setDirection] = useState("ALL"); // UP, DOWN, ALL
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 2000; // 2 seconds
    
    console.log(`[Frontend] 🚀 Starting data fetch (attempt ${retryCount + 1}/${MAX_RETRIES + 1})...`);
    setLoading(true);
    setError("");
    
    try {
      // Simple GET request to verify connectivity
      const healthCheck = await fetch(`${API_BASE_URL}/`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!healthCheck.ok) {
        throw new Error(`Backend returned ${healthCheck.status}`);
      }
      
      console.log('[Frontend] ✅ Backend is accessible');
      
      // Make the actual request with minimal options
      const response = await fetch(`${API_BASE_URL}/breakouts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          stock_list: []
        })
      });
      
      const responseTime = Date.now();
      const responseData = await response.json();
      
      console.log(`[Frontend] 📥 Response received in ${responseTime}ms`, {
        status: response.status,
        statusText: response.status,
        data: responseData
      });
      
      if (!response.ok) {
        throw new Error(responseData.message || `Server error: ${response.status}`);
      }
      
      // Process and set the data
      if (responseData && Array.isArray(responseData.data)) {
        setStocks(responseData.data);
      } else {
        throw new Error('Invalid data format received from server');
      }
    } catch (err) {
      console.error('[Frontend] ❌ Error:', err);
      
      // Retry logic for network errors
      if (retryCount < MAX_RETRIES && 
          (err.message.includes('Network') || err.message.includes('Failed to fetch'))) {
        console.log(`[Frontend] 🔄 Retrying in ${RETRY_DELAY/1000} seconds...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return fetchData(retryCount + 1);
      }
      
      setError(`Failed to load data: ${err.message}`);
      setStocks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load and when filter changes
  useEffect(() => {
    fetchData();
  }, [filter, fetchData]);

  // Apply direction + search filters on client
  const filteredStocks = useMemo(() => {
    let list = stocks;
    if (direction === "UP") list = list.filter((s) => s.chart === "up");
    else if (direction === "DOWN") list = list.filter((s) => s.chart === "down");

    if (search.trim()) {
      const q = search.trim().toUpperCase();
      list = list.filter((s) => s.symbol?.toUpperCase().includes(q));
    }
    return list;
  }, [stocks, direction, search]);

  return (
    <div className="highlow-wrapper">
      <header className="momentum-header">
        <div className="momentum-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U" />
          </div>
        </div>
      </header>

      <header className="hl-header s4u-title-row">
        <div className="s4u-page-heading">
          <button className="momentum-back-btn s4u-back-btn" onClick={onBack}>
            ← Back to Dashboard
          </button>
          <h1 className="hl-title">Momentum Radar</h1>
        </div>
        <div className="hl-filters">
          <RefreshButton onRefresh={fetchData} isLoading={loading} />
        </div>
      </header>

      <section className="hl-category">
        <div className="category-card">
          <h2>2 DAY HIGH &amp; LOW</h2>
          <p className="how-to">How to use ?</p>

          <button
            className={`dir-btn up ${direction === "UP" ? "active" : ""}`}
            onClick={() => setDirection("UP")}
            disabled={loading}
          >
            Up
          </button>
          <button
            className={`dir-btn down ${direction === "DOWN" ? "active" : ""}`}
            onClick={() => setDirection("DOWN")}
            disabled={loading}
          >
            Down
          </button>
          <button
            className={`dir-btn all ${direction === "ALL" ? "active" : ""}`}
            onClick={() => setDirection("ALL")}
            disabled={loading}
          >
            All
          </button>
        </div>
      </section>

      <section className="hl-table-section">
        <div className="hl-table">
          <div className="hl-row hl-header-row">
            <div className="hl-cell">⭐</div>
            <div className="hl-cell">Name</div>
            <div className="hl-cell">Date &amp; Time</div>
            <div className="hl-cell">Percentage</div>
            <div className="hl-cell">Current Percentage</div>
            <div className="hl-cell">SIGNALS</div>
          </div>

          {loading ? (
            <div className="hl-row">
              <div className="hl-cell">Loading...</div>
            </div>
          ) : error ? (
            <div className="hl-row">
              <div className="hl-cell">{error}</div>
            </div>
          ) : filteredStocks.length === 0 ? (
            <div className="hl-row">
              <div className="hl-cell">No results</div>
            </div>
          ) : (
            filteredStocks.map((stock) => (
              <div key={stock.id} className="hl-row">
                <div className="hl-cell">
                  <WatchlistStar 
                    stock={{
                      symbol: stock.symbol,
                      name: stock.symbol,
                      breakout_time: stock.breakout_time,
                      breakout_pct: stock.breakout_pct,
                      after_bo: stock.after_bo,
                      chart: stock.chart
                    }} 
                    size="18px"
                  />
                </div>
                <div className="hl-cell" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <StockLogo symbol={stock.symbol} size={20} />
                  {stock.symbol}
                </div>
                <div className="hl-cell">{stock.breakout_time}</div>
                <div className="hl-cell">
                  <span className={stock.breakout_pct && !String(stock.breakout_pct).includes('-') ? 'positive' : 'negative'}>
                    {stock.breakout_pct !== "" ? `${String(stock.breakout_pct).includes('-') ? '' : '+'}${stock.breakout_pct}%` : "—"}
                  </span>
                </div>
                <div className="hl-cell">
                  <span className={stock.after_bo && !String(stock.after_bo).includes('-') ? 'positive' : 'negative'}>
                    {stock.after_bo !== "" ? `${String(stock.after_bo).includes('-') ? '' : '+'}${stock.after_bo}%` : "—"}
                  </span>
                </div>
                <div className="hl-cell hl-signal-cell">
                  {stock.chart === "up" ? (
                    <span className="signal-up" style={{ color: '#10b981', fontSize: '1.2rem' }}>↑</span>
                  ) : stock.chart === "down" ? (
                    <span className="signal-down" style={{ color: '#ef4444', fontSize: '1.2rem' }}>↓</span>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default HighLowPage;
